import * as React from 'react'
import { Dropdown, Popover, PopoverPlacements } from '@zooz/generic-ui-components'
import difference from 'lodash/difference'
import uniq from 'lodash/uniq'
import { useDispatch, useSelector } from 'react-redux'

import localStorageAccessor from '../../../../helpers/localStorageAccessor/localStorageAccessor'
import dismissLanguages from '../../dismissLanguages'
import {
  getIsLocalizationLoading,
  getIsLocalizedInit,
  getLanguagesByStatus,
  getSelectedLanguage
} from '../../redux/selectors'
import { actions } from '../../redux/slice'
import languageIcon from './language-icon.svg'
import NewLanguageMessage from './NewLanguageMessage'

import styles from './LanguageSelection.scss'

const initialIgnoredLanguages = ['en-US']

const getIgnoredLanguageCodes = (): string[] => {
  let ignoredLanguages: string[] = initialIgnoredLanguages
  try {
    ignoredLanguages = localStorageAccessor.dismissedLanguages
    if (ignoredLanguages.length === 0) {
      ignoredLanguages = initialIgnoredLanguages
    }
  } catch (_e) {
    ignoredLanguages = initialIgnoredLanguages
  }

  return uniq([...ignoredLanguages, ...initialIgnoredLanguages])
}

export interface LanguageSelectionProps {
  showNewLanguageTooltip?: boolean
}

const LanguageSelection: React.FC<LanguageSelectionProps> = ({ showNewLanguageTooltip }) => {
  const dispatch = useDispatch()
  const selectedLanguage = useSelector(getSelectedLanguage)
  const languages = useSelector(getLanguagesByStatus)
  const isLocalizeInitialized = useSelector(getIsLocalizedInit)
  const isLocalizeLoading = useSelector(getIsLocalizationLoading)
  const [newLanguagesCodes, setNewLanguagesCodes] = React.useState([])

  const options = []
  let selectedLanguageDisplayName = ''
  for (const { code, displayName } of languages) {
    if (code !== selectedLanguage) {
      options.push({
        key: code,
        value: <span className={styles.languageText}>{displayName}</span>
      })
    } else {
      selectedLanguageDisplayName = displayName
    }
  }

  React.useEffect(() => {
    dispatch(actions.initLocalize())
  }, [dispatch])
  React.useEffect(() => {
    const newLanguagesNotIgnored = difference(languages.map(language => language.code), getIgnoredLanguageCodes())
    if (newLanguagesNotIgnored.length) {
      setNewLanguagesCodes(newLanguagesNotIgnored)
    }
  }, [languages])

  const dismissPopUp = (): void => {
    dismissLanguages()
    setNewLanguagesCodes([])
  }

  return (
    <div className={styles.wrapper}>
      <Popover
        content={(
          <NewLanguageMessage
            dismiss={dismissPopUp}
            languages={languages}
            newLanguagesCodes={newLanguagesCodes}
          />
        )}
        offset={10}
        isOpened={!!showNewLanguageTooltip && newLanguagesCodes.length > 0}
        placement={PopoverPlacements.BottomRight}
      >
        <Dropdown
          onOpen={dismissPopUp}
          options={options}
          selectedOption={{
            key: selectedLanguage,
            value: (
              <span className={styles.selected}>
                <img className={styles.selected__icon} src={languageIcon} width='15' height='15' />
                <span className={styles.languageText}>{selectedLanguageDisplayName}</span>
              </span>
            )
          }}
          disabled={!isLocalizeInitialized || isLocalizeLoading || options.length === 0}
          enableFilter={false}
          onChange={({ key }) => {
            dispatch(actions.setLocalizationPreferences({ language: key }))
          }}
        />
      </Popover>
    </div>
  )
}

export default LanguageSelection
